import { Link } from '@inertiajs/react'
import { MessageCircle, Sparkles } from 'lucide-react'

import { AppShell } from '@/layouts/AppShell'
import { PageHeader } from '@/components/layout/PageHeader'
import { Card } from '@/components/ui/Card'
import { EmptyState } from '@/components/ui/EmptyState'
import { buttonClasses } from '@/components/ui/Button'
import type { Breadcrumb } from '@/types/shared'

type ChatroomSummary = {
  id: string
  schoolName: string | null
  coachName: string
  lastMessage: string | null
  lastMessageAt: string | null
  lastMessageSender: string | null
}

type ChatroomsIndexProps = {
  chatrooms: ChatroomSummary[]
  breadcrumbs: Breadcrumb[]
}

export default function PlayerChatroomsIndex({ chatrooms, breadcrumbs }: ChatroomsIndexProps) {
  return (
    <AppShell>
      <PageHeader
        title="チャット"
        description="高校・担当コーチとのメッセージ履歴を確認し、次の一手を考えましょう。"
        breadcrumbs={breadcrumbs}
        actions={
          <Link href="/player/profile/edit" className={buttonClasses({})}>
            プロフィールを磨く
          </Link>
        }
      />

      {chatrooms.length === 0 ? (
        <EmptyState
          icon={<Sparkles className="h-12 w-12" />}
          title="まだチャットはありません"
          description="プロフィールを更新し、学校コーチにアピールすることでチャットリクエストが増えます。"
          action={<Link href="/player/profile/edit" className={buttonClasses({})}>自己PRを充実させる</Link>}
        />
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {chatrooms.map((room) => (
            <Card key={room.id} className="relative overflow-hidden border border-white/5 bg-white/5 p-6">
              <div className="absolute -right-8 -top-8 h-24 w-24 rounded-full bg-brand-500/10" />
              <div className="relative flex flex-col gap-4">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="text-lg font-semibold text-white">{room.schoolName || '未登録の学校'}</h3>
                    <p className="text-sm text-white/60">{room.coachName}</p>
                  </div>
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-500/20 text-brand-100">
                    <MessageCircle className="h-5 w-5" />
                  </div>
                </div>
                <div className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white/70">
                  {room.lastMessage ? (
                    <>
                      <span className="text-xs text-white/40">{room.lastMessageSender || 'メッセージ'}</span>
                      <p className="mt-1 line-clamp-2 text-sm">{room.lastMessage}</p>
                    </>
                  ) : (
                    <p>まだ最初のメッセージはありません。</p>
                  )}
                </div>
                <div className="flex items-center justify-between text-xs text-white/40">
                  {room.lastMessageAt ? new Date(room.lastMessageAt).toLocaleString('ja-JP') : '履歴なし'}
                  <Link
                    href={`/player/chatrooms/${room.id}`}
                    className={buttonClasses({ variant: 'secondary', size: 'sm' })}
                  >
                    開く
                  </Link>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </AppShell>
  )
}
